import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';

// About you — the answers that buy back daily questions.
//
// A trait is answered once and then stops a question from ever being asked
// again: tell us you don't drink and your alcohol column fills itself with a
// confirmed zero every day for the rest of time, without you ever seeing the
// question. That trade is the whole point of the screen, so each row says out
// loud what it silences rather than leaving the user to guess why we're asking.
//
// "Not sure" genuinely clears the answer rather than storing a no — an unknown
// trait silences nothing, because not knowing and knowing-the-answer-is-no are
// different things and only one of them should fill in data on your behalf.

const TraitEditor = ({ onChanged }) => {
  const [traits, setTraits] = useState(null);
  const [setup, setSetup] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/api/interview/traits');
      setTraits(data.traits || []);
      setSetup(data.setup || null);
    } catch {
      setTraits([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (trait, value) => {
    setBusy(trait.key);
    try {
      if (value === null) {
        await apiClient.delete(`/api/interview/traits/${trait.key}`);
      } else {
        await apiClient.put(`/api/interview/traits/${trait.key}`, { value });
      }
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save that');
    } finally {
      setBusy(null);
    }
  };

  if (!traits) return null;

  const essential = traits.filter((t) => t.essential);
  const optional = traits.filter((t) => !t.essential);

  const row = (trait) => {
    const options = trait.value_type === 'bool'
      ? [{ label: 'Yes', value: true }, { label: 'No', value: false }]
      : trait.choices.map((c) => ({ label: c.replace(/_/g, ' '), value: c }));

    return (
      <div key={trait.key}
           className="border-t border-gray-200 dark:border-surface-dark-border py-3
                      first:border-t-0 first:pt-0">
        <p className="text-base text-gray-900 dark:text-gray-100">{trait.question}</p>
        {trait.silences?.length > 0 && (
          <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
            Answering this removes {trait.silences.length} question
            {trait.silences.length === 1 ? '' : 's'} from your daily check-in.
          </p>
        )}
        <div className="mt-2 flex flex-wrap gap-2">
          {options.map((opt) => (
            <Button
              key={String(opt.value)}
              size="sm"
              disabled={busy === trait.key}
              variant={trait.answered && trait.value === opt.value ? 'default' : 'outline'}
              onClick={() => save(trait, opt.value)}
            >
              {opt.label}
            </Button>
          ))}
          {trait.answered && (
            <Button size="sm" variant="ghost" disabled={busy === trait.key}
                    onClick={() => save(trait, null)}>
              Not sure
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card className="p-4 sm:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          About you
        </h2>
        {setup && (
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {setup.answered} of {setup.total} answered
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
        Things that stay true. Each one you answer is a question we stop asking
        you every day — and a column that fills itself in from then on.
      </p>

      <div className="mt-4">
        {essential.map(row)}
      </div>

      {optional.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm font-medium
                              text-gray-900 dark:text-gray-100">
            More about you ({optional.length})
          </summary>
          <div className="mt-3">{optional.map(row)}</div>
        </details>
      )}
    </Card>
  );
};

export default TraitEditor;
